import { v4 as uuidv4 } from "uuid";
import { supabase } from "@/integrations/supabase/client";
import { computeFileHash } from "@/utils/fileHash";
import { uploadFile, type UploadResult } from "@/stores/uploadQueue/repository";
import { mirrorToBackups, rowOwnsUploadedObject } from "@/stores/uploadQueue/backupMirror";
import { resolveContentType } from "../constants/fileTypes";
import {
  BuildingStorageQuotaError,
  cancelBuildingStorageUpload,
  invalidateBuildingStorageQuota,
  reserveBuildingStorageUploads,
  storageAdmissionErrorFrom,
  storageQuotaErrorMessage,
} from "./buildingStorageQuota";
import { capture } from "@/lib/sentry/capture";
import type { PostgrestError } from "@supabase/supabase-js";
import { discardBuildingStorageUpload } from "./discardBuildingStorageUpload";

const BUCKET = "documents";

/**
 * Path segment under `${orgId}/${buildingId}/` that keeps a client-generated
 * document out of the flat upload namespace. The uuid is what actually makes
 * the key unique, as everywhere else.
 */
export type GeneratedKeyPrefix = "forms" | "protocols";

export interface FileIntoProjectInput {
  file: File;
  orgId: string;
  buildingId: number;
  projectId: number;
  /** `null` = the project root — the value the RPC and ChatbotFilesPanel pass. */
  folderId: number | null;
  /** `user_profiles.id` of the caller → `p_created_by`. */
  userId: number;
  source: string;
  keyPrefix: GeneratedKeyPrefix;
  /** Fires after the RPC confirms a document row, before the backup mirror. */
  onCreated?: () => void;
}

export interface FiledDocument {
  id: number;
  filename: string;
  size: number;
}

/** The create RPC may have committed; its uploaded object and quota hold must stay intact. */
export class UnconfirmedDocumentRegistrationError extends Error {
  constructor() {
    super(
      "Document registration could not be confirmed. Check the project before retrying; the upload was retained."
    );
    this.name = "UnconfirmedDocumentRegistrationError";
  }
}

function storageKey(
  orgId: string,
  buildingId: number,
  filename: string,
  keyPrefix: GeneratedKeyPrefix
): string {
  const extension = filename.split(".").pop() || "bin";
  return `${orgId}/${buildingId}/${keyPrefix}/${uuidv4()}.${extension}`;
}

function uploadFailureMessage(result: UploadResult, quotaMessage: string | null): string {
  if (quotaMessage) return quotaMessage;
  if (result.error != null) return result.error;
  return result.errorCode === "STORAGE_ADMISSION" ? "Storage admission rejected" : "upload failed";
}

function uploadFailure(result: UploadResult): Error {
  const quotaMessage = storageQuotaErrorMessage(result.error ?? "");
  const message = uploadFailureMessage(result, quotaMessage);
  if (result.errorCode === "STORAGE_ADMISSION" || quotaMessage) {
    return new BuildingStorageQuotaError(message);
  }
  return new Error(message);
}

function registrationFailure(error: PostgrestError): Error {
  return storageAdmissionErrorFrom(error) ?? new Error(error.message);
}

async function releaseStorageReservation(
  reservationId: string,
  input: FileIntoProjectInput
): Promise<void> {
  try {
    await cancelBuildingStorageUpload(reservationId, input.buildingId);
  } catch (error) {
    capture(error instanceof Error ? error : new Error(String(error)), {
      tags: { feature: "building-storage-quota", stage: "cancel-upload" },
      fingerprint: ["building-storage-quota", "cancel-upload"],
      context: {
        quota: { buildingId: input.buildingId, source: input.source, keyPrefix: input.keyPrefix },
      },
    });
  } finally {
    invalidateBuildingStorageQuota(input.buildingId);
  }
}

async function createDocumentRecord(
  input: FileIntoProjectInput,
  path: string,
  contentHash: string
) {
  const { data, error } = await supabase.rpc("create_document_with_versioning", {
    p_org_id: input.orgId,
    p_building_id: input.buildingId,
    p_project_id: input.projectId,
    // Null means the project root — the same value ChatbotFilesPanel passes.
    p_folder_id: input.folderId,
    p_filename: input.file.name,
    p_file_path: path,
    p_content_type: resolveContentType(input.file),
    p_size: input.file.size,
    p_content_hash: contentHash,
    p_created_by: input.userId,
    p_source: input.source,
  });
  return { data, error };
}

type RegisteredDocumentAction = "created" | "versioned" | "skipped" | "resurrected";

function isRegisteredDocumentAction(value: unknown): value is RegisteredDocumentAction {
  return (
    value === "created" || value === "versioned" || value === "skipped" || value === "resurrected"
  );
}

function isPositiveDocumentId(value: unknown): value is number {
  return typeof value === "number" && Number.isSafeInteger(value) && value > 0;
}

function registeredDocument(data: unknown): { id: number; action: RegisteredDocumentAction } {
  if (typeof data !== "object" || data === null) {
    throw new Error("Document registration returned no valid document row");
  }
  const row = data as Record<string, unknown>;
  if (!isPositiveDocumentId(row.document_id) || !isRegisteredDocumentAction(row.action)) {
    throw new Error("Document registration returned no valid document row");
  }
  return { id: row.document_id, action: row.action };
}

/**
 * File an in-memory document into a project folder: storage object first, then
 * the row that owns it, then the backup copy.
 *
 * The order is load-bearing and is why this lives in one place rather than
 * being re-typed per caller — see the comments inside.
 */
export async function fileIntoProject(input: FileIntoProjectInput): Promise<FiledDocument> {
  const { file } = input;
  const path = storageKey(input.orgId, input.buildingId, file.name, input.keyPrefix);
  const contentHash = (await computeFileHash(file)) ?? "";
  const [reservation] = await reserveBuildingStorageUploads(input.buildingId, [
    { idempotencyKey: uuidv4(), path, size: file.size },
  ]);
  let reservationId: string | null = reservation.reservationId;
  let objectUploaded = false;
  let registrationAttempted = false;
  let knownUnowned = false;
  const releaseHeldReservation = async () => {
    const heldId = reservationId;
    reservationId = null;
    if (heldId) await releaseStorageReservation(heldId, input);
  };

  try {
    const uploadResult = await uploadFile(file, path, BUCKET);
    if (!uploadResult.success) throw uploadFailure(uploadResult);
    objectUploaded = true;

    registrationAttempted = true;
    const { data, error } = await createDocumentRecord(input, path, contentHash);
    if (error) {
      knownUnowned = storageAdmissionErrorFrom(error) !== null;
      throw registrationFailure(error);
    }
    const document = registeredDocument(data);
    const ownsUploadedObject = rowOwnsUploadedObject(document.action);
    if (ownsUploadedObject) {
      reservationId = null;
      invalidateBuildingStorageQuota(input.buildingId);
    } else {
      knownUnowned = true;
    }

    // The RPC confirmed a row. Say so before the mirror, which can still fail.
    input.onCreated?.();

    // Skipped and resurrected rows keep their previous object path; this hold
    // belongs to an unreferenced upload and must be released, even on success.
    if (!ownsUploadedObject && reservationId) {
      await discardBuildingStorageUpload(path, reservationId, input.buildingId);
      reservationId = null;
    }

    // A backup is permanent, so only mirror when the row owns this path.
    if (ownsUploadedObject) await mirrorToBackups(path);

    return { id: document.id, filename: file.name, size: file.size };
  } catch (error) {
    if (reservationId && knownUnowned && objectUploaded) {
      await discardBuildingStorageUpload(path, reservationId, input.buildingId);
      reservationId = null;
    } else if (reservationId && !objectUploaded) {
      await releaseHeldReservation();
    } else if (reservationId && registrationAttempted) {
      capture(error instanceof Error ? error : new Error(String(error)), {
        tags: { feature: "building-storage-quota", stage: "registration-unconfirmed" },
        fingerprint: ["building-storage-quota", "registration-unconfirmed"],
        context: { quota: { buildingId: input.buildingId, path, source: input.source } },
      });
      throw new UnconfirmedDocumentRegistrationError();
    }
    throw error;
  }
}
